import { aesGcmDecrypt, aesGcmEncrypt } from '../crypto/crypto'
import { normalizeDocumentMetadata } from '../pages/documentMetadata'
import { localHLC } from './hlc'
import type { SettingChange, TreeChange } from './syncEngine'

// What tree and settings rows look like on the wire: names, icons, colors,
// metadata and setting values are encrypted with the master key on the way
// out and decrypted on the way in. The server never sees them in the clear.

/** A tree change as the server takes it, with its text fields encrypted. */
export async function encryptTreeChange(c: TreeChange, masterKey: CryptoKey | null) {
  let titleEncrypted = c.titleEncrypted ?? null
  let titleNonce = c.titleNonce ?? null
  let iconEncrypted = c.iconEncrypted ?? null
  let iconNonce = c.iconNonce ?? null
  let colorEncrypted = c.colorEncrypted ?? null
  let colorNonce = c.colorNonce ?? null
  let metadataEncrypted = c.metadataEncrypted ?? null
  let metadataNonce = c.metadataNonce ?? null

  if (c.title && masterKey && !titleEncrypted) {
    const blob = await aesGcmEncrypt(masterKey, new TextEncoder().encode(c.title))
    titleEncrypted = blob.ciphertext
    titleNonce = blob.iv
  }

  if (c.icon && masterKey && !iconEncrypted) {
    const blob = await aesGcmEncrypt(masterKey, new TextEncoder().encode(c.icon))
    iconEncrypted = blob.ciphertext
    iconNonce = blob.iv
  }

  if (c.color && masterKey && !colorEncrypted) {
    const blob = await aesGcmEncrypt(masterKey, new TextEncoder().encode(c.color))
    colorEncrypted = blob.ciphertext
    colorNonce = blob.iv
  }

  if (c.metadata && masterKey && !metadataEncrypted) {
    const blob = await aesGcmEncrypt(masterKey, new TextEncoder().encode(JSON.stringify(c.metadata)))
    metadataEncrypted = blob.ciphertext
    metadataNonce = blob.iv
  }

  const subtype = c.subtype ?? (c.type === 'folder' ? 'folder' : 'page')
  const type = c.type ?? (subtype === 'page' ? 'document' : 'folder')

  return {
    id: c.id,
    parentId: c.parentId,
    titleEncrypted,
    titleNonce,
    type,
    subtype,
    iconEncrypted,
    iconNonce,
    colorEncrypted,
    colorNonce,
    metadataEncrypted,
    metadataNonce,
    position: c.position,
    hlcTimestamp: c.hlcTimestamp ?? localHLC.now(),
    deleted: !!c.deleted,
  }
}

/** A tree row from the server, decrypted. A name that can't be read comes back as '[Encrypted]'. */
export async function decryptTreeNode(node: any, masterKey: CryptoKey | null) {
  const subtype: 'desk' | 'folder' | 'page' = node.subtype || (node.type === 'folder' ? 'folder' : 'page')

  let name = 'Untitled'
  if (node.title_encrypted && node.title_nonce && masterKey) {
    try {
      const plainBytes = await aesGcmDecrypt(masterKey, {
        ciphertext: node.title_encrypted,
        iv: node.title_nonce,
      })
      name = new TextDecoder().decode(plainBytes)
    } catch {
      name = '[Encrypted]'
    }
  }

  let icon: string | null = null
  if (node.icon_encrypted && node.icon_nonce && masterKey) {
    try {
      const plainBytes = await aesGcmDecrypt(masterKey, {
        ciphertext: node.icon_encrypted,
        iv: node.icon_nonce,
      })
      icon = new TextDecoder().decode(plainBytes)
    } catch {
      icon = null
    }
  }

  let color: string | null = null
  if (node.color_encrypted && node.color_nonce && masterKey) {
    try {
      const plainBytes = await aesGcmDecrypt(masterKey, {
        ciphertext: node.color_encrypted,
        iv: node.color_nonce,
      })
      color = new TextDecoder().decode(plainBytes)
    } catch {
      color = null
    }
  }

  let metadata: TreeChange['metadata'] | undefined
  if (node.metadata_encrypted && node.metadata_nonce && masterKey) {
    try {
      const plainBytes = await aesGcmDecrypt(masterKey, {
        ciphertext: node.metadata_encrypted,
        iv: node.metadata_nonce,
      })
      metadata = normalizeDocumentMetadata(JSON.parse(new TextDecoder().decode(plainBytes)))
    } catch {
      metadata = undefined
    }
  }

  return {
    id: node.id,
    name,
    type: subtype === 'page' ? 'doc' : 'folder',
    subtype,
    icon,
    color,
    metadata,
    parentId: node.parent_id,
    position: node.position,
    hlcTimestamp: node.hlc_timestamp,
    deletedAt: node.deleted_at,
  }
}

/** A setting change as the server takes it, its value encrypted (left empty while the key is locked). */
export async function encryptSettingChange(c: SettingChange, masterKey: CryptoKey | null) {
  const jsonVal = JSON.stringify(c.value)
  let valueEncrypted = ''
  let valueNonce = ''

  if (masterKey) {
    const blob = await aesGcmEncrypt(masterKey, new TextEncoder().encode(jsonVal))
    valueEncrypted = blob.ciphertext
    valueNonce = blob.iv
  }

  return {
    key: c.key,
    valueEncrypted,
    valueNonce,
    hlcTimestamp: c.hlc ?? localHLC.now(),
  }
}

/**
 * A setting row's value, decrypted; null when it has none or there is no key.
 * `failed` says decryption itself went wrong, so the row must be fetched again.
 */
export async function decryptSettingValue(row: any, masterKey: CryptoKey | null): Promise<{ value: any; failed: boolean }> {
  if (!row.setting_value_encrypted || !row.setting_value_nonce || !masterKey) return { value: null, failed: false }
  try {
    const plainBytes = await aesGcmDecrypt(masterKey, {
      ciphertext: row.setting_value_encrypted,
      iv: row.setting_value_nonce,
    })
    return { value: JSON.parse(new TextDecoder().decode(plainBytes)), failed: false }
  } catch {
    return { value: null, failed: true }
  }
}
